/**
 * P2P-first transport: a direct RTCDataChannel between the two devices of
 * a 1:1 conversation, negotiated with signaling ENCRYPTED by the double ratchet
 * (`p2p` control → the relay only sees opaque ciphertext: never the SDP nor the
 * DTLS fingerprint). When the channel is open, the envelopes (already ratcheted)
 * travel DIRECT from device to device and the relay does not even see the
 * ciphertext; if the channel is not available, the send degrades to the blind mailbox
 * (store-and-forward) without losing anything.
 *
 * Deterministic anti-glare: only the device with the lexicographically SMALLER id
 * starts the offer; the other asks for the connection with a `want` signal. This way there are never
 * two crossed offers.
 *
 * `RTCPeerConnection` is INJECTABLE (same as in calls): in
 * production it is the browser global; in test a fake pair is injected.
 */

import { decodeEnvelope, encodeEnvelope, type WireEnvelope } from "@susurro/protocol";

import { parseIceCandidate, parseSessionDescription } from "./call";

/** Negotiation signal of the P2P channel (travels encrypted inside the 1:1 ratchet). */
export type P2pSignal =
  | { readonly kind: "want" }
  | { readonly kind: "offer"; readonly sdp: string }
  | { readonly kind: "answer"; readonly sdp: string }
  | { readonly kind: "ice"; readonly candidate: string }
  | { readonly kind: "close" };

/** Structural subset of `RTCDataChannel` (to inject a fake in test). */
export interface DataChannelLike {
  readyState: string;
  addEventListener(type: string, listener: (ev: unknown) => void): void;
  send(data: string): void;
  close(): void;
}

/** Subset of `RTCPeerConnection` for DataChannels (injectable in test). */
export interface DataPeerConnectionLike {
  addEventListener(type: string, listener: (ev: unknown) => void): void;
  createDataChannel(label: string): DataChannelLike;
  createOffer(): Promise<RTCSessionDescriptionInit>;
  createAnswer(): Promise<RTCSessionDescriptionInit>;
  setLocalDescription(desc: RTCSessionDescriptionInit): Promise<void>;
  setRemoteDescription(desc: RTCSessionDescriptionInit): Promise<void>;
  addIceCandidate(candidate: RTCIceCandidateInit): Promise<void>;
  close(): void;
}

interface P2pDeps {
  readonly selfDeviceId: string;
  /** Sends a negotiation signal to the peer (through the ratchet → blind relay). */
  readonly sendSignal: (peer: string, signal: P2pSignal) => Promise<void> | void;
  /** Incoming envelope received over the direct channel (already encrypted by the ratchet). */
  readonly onEnvelope: (envelope: WireEnvelope) => void;
  readonly createPeerConnection: () => DataPeerConnectionLike;
  /** State change of the link with a peer (for the transport badge). */
  readonly onStateChange?: (peer: string, connected: boolean) => void;
  readonly now?: () => number;
}

interface Link {
  pc: DataPeerConnectionLike;
  channel?: DataChannelLike;
  remoteReady: boolean;
  connected: boolean;
  readonly iceQueue: RTCIceCandidateInit[];
}

/** We do not re-attempt negotiating with the same peer more often than this. */
const ATTEMPT_THROTTLE_MS = 10_000;

function readProp(ev: unknown, key: string): unknown {
  return typeof ev === "object" && ev !== null ? Reflect.get(ev, key) : undefined;
}

/** Structural guard of the channel arriving in the `datachannel` event (without `as`). */
function isDataChannelLike(value: unknown): value is DataChannelLike {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof readProp(value, "readyState") === "string" &&
    typeof readProp(value, "addEventListener") === "function" &&
    typeof readProp(value, "send") === "function" &&
    typeof readProp(value, "close") === "function"
  );
}

export class P2PManager {
  private readonly links = new Map<string, Link>();
  private readonly lastAttempt = new Map<string, number>();
  private readonly now: () => number;

  constructor(private readonly deps: P2pDeps) {
    this.now = deps.now ?? (() => Date.now());
  }

  /** Are we the deterministic initiator against `peer`? (smaller id starts). */
  private isInitiator(peer: string): boolean {
    return this.deps.selfDeviceId < peer;
  }

  /** Is there a direct channel open with `peer`? */
  connected(peer: string): boolean {
    return this.links.get(peer)?.connected === true;
  }

  /**
   * Ensures (best-effort) a P2P link with `peer`: the initiator opens the
   * offer; the other side asks with `want`. Failures (peer with no session yet,
   * relay down…) are swallowed: the chat keeps working through the mailbox.
   */
  async ensureLink(peer: string): Promise<void> {
    if (peer === this.deps.selfDeviceId || this.links.has(peer)) {
      return;
    }
    const last = this.lastAttempt.get(peer) ?? 0;
    if (this.now() - last < ATTEMPT_THROTTLE_MS) {
      return;
    }
    this.lastAttempt.set(peer, this.now());
    try {
      if (this.isInitiator(peer)) {
        await this.openOffer(peer);
      } else {
        await this.deps.sendSignal(peer, { kind: "want" });
      }
    } catch (err) {
      // No session/relay yet: it is retried after the throttle.
      console.warn("[p2p] deferred negotiation", err);
      this.teardown(peer);
    }
  }

  /** Entry point of every `p2p` signal received from the peer (the engine calls it). */
  async handleSignal(peer: string, signal: P2pSignal): Promise<void> {
    try {
      switch (signal.kind) {
        case "want": {
          // Only the deterministic initiator responds by opening the offer.
          if (this.isInitiator(peer) && !this.links.has(peer)) {
            this.lastAttempt.set(peer, this.now());
            await this.openOffer(peer);
          }
          break;
        }
        case "offer": {
          await this.onOffer(peer, signal.sdp);
          break;
        }
        case "answer": {
          await this.onAnswer(peer, signal.sdp);
          break;
        }
        case "ice": {
          await this.onIce(peer, signal.candidate);
          break;
        }
        case "close": {
          this.teardown(peer);
          break;
        }
      }
    } catch (err) {
      console.warn("[p2p] unprocessable signal", err);
      this.teardown(peer);
    }
  }

  /**
   * Sends an envelope over the direct channel if it is open. Returns `false` if there
   * is no channel (the caller degrades to the relay mailbox). A send failure closes
   * the link so that the following envelopes degrade cleanly.
   */
  trySend(peer: string, envelope: WireEnvelope): boolean {
    const link = this.links.get(peer);
    if (!link?.connected || !link.channel || link.channel.readyState !== "open") {
      return false;
    }
    try {
      link.channel.send(encodeEnvelope(envelope));
      return true;
    } catch (err) {
      console.warn("[p2p] canal roto; degradando al relay", err);
      this.teardown(peer);
      return false;
    }
  }

  /** Closes all links (provider cleanup). */
  closeAll(): void {
    for (const peer of [...this.links.keys()]) {
      this.teardown(peer);
    }
  }

  // --- Negotiation ----------------------------------------------------------

  private async openOffer(peer: string): Promise<void> {
    const link = this.buildLink(peer);
    const channel = link.pc.createDataChannel("susurro");
    this.adoptChannel(peer, link, channel);
    const offer = await link.pc.createOffer();
    await link.pc.setLocalDescription(offer);
    await this.deps.sendSignal(peer, { kind: "offer", sdp: JSON.stringify(offer) });
  }

  private async onOffer(peer: string, sdp: string): Promise<void> {
    // New offer from the initiator (e.g. after a restart): replaces the link.
    if (this.links.has(peer)) {
      this.teardown(peer);
    }
    this.lastAttempt.set(peer, this.now());
    const link = this.buildLink(peer);
    link.pc.addEventListener("datachannel", (ev) => {
      const channel = readProp(ev, "channel");
      if (isDataChannelLike(channel)) {
        this.adoptChannel(peer, link, channel);
      }
    });
    await link.pc.setRemoteDescription(parseSessionDescription(sdp));
    link.remoteReady = true;
    await this.flushIce(link);
    const answer = await link.pc.createAnswer();
    await link.pc.setLocalDescription(answer);
    await this.deps.sendSignal(peer, { kind: "answer", sdp: JSON.stringify(answer) });
  }

  private async onAnswer(peer: string, sdp: string): Promise<void> {
    const link = this.links.get(peer);
    if (!link) {
      return;
    }
    await link.pc.setRemoteDescription(parseSessionDescription(sdp));
    link.remoteReady = true;
    await this.flushIce(link);
  }

  private async onIce(peer: string, candidateJson: string): Promise<void> {
    const link = this.links.get(peer);
    if (!link) {
      return;
    }
    const candidate = parseIceCandidate(candidateJson);
    if (link.remoteReady) {
      await link.pc.addIceCandidate(candidate);
    } else {
      link.iceQueue.push(candidate);
    }
  }

  private buildLink(peer: string): Link {
    const pc = this.deps.createPeerConnection();
    const link: Link = { pc, remoteReady: false, connected: false, iceQueue: [] };
    this.links.set(peer, link);
    pc.addEventListener("icecandidate", (ev) => {
      const candidate = readProp(ev, "candidate");
      if (candidate) {
        // JSON.stringify invokes RTCIceCandidate.toJSON automatically.
        void Promise.resolve(
          this.deps.sendSignal(peer, { kind: "ice", candidate: JSON.stringify(candidate) }),
        ).catch(() => undefined);
      }
    });
    pc.addEventListener("connectionstatechange", () => {
      const state = readProp(pc, "connectionState");
      if (state === "failed" || state === "closed" || state === "disconnected") {
        this.teardown(peer);
      }
    });
    return link;
  }

  private adoptChannel(peer: string, link: Link, channel: DataChannelLike): void {
    link.channel = channel;
    channel.addEventListener("open", () => {
      link.connected = true;
      this.deps.onStateChange?.(peer, true);
    });
    channel.addEventListener("close", () => {
      this.teardown(peer);
    });
    channel.addEventListener("message", (ev) => {
      const data = readProp(ev, "data");
      if (typeof data !== "string") {
        return;
      }
      try {
        this.deps.onEnvelope(decodeEnvelope(data));
      } catch (err) {
        console.warn("[p2p] invalid direct envelope", err);
      }
    });
  }

  private async flushIce(link: Link): Promise<void> {
    while (link.iceQueue.length > 0) {
      const candidate = link.iceQueue.shift()!;
      await link.pc.addIceCandidate(candidate);
    }
  }

  private teardown(peer: string): void {
    const link = this.links.get(peer);
    if (!link) {
      return;
    }
    this.links.delete(peer);
    const wasConnected = link.connected;
    try {
      link.channel?.close();
    } catch {
      /* double close */
    }
    try {
      link.pc.close();
    } catch {
      /* double close */
    }
    if (wasConnected) {
      this.deps.onStateChange?.(peer, false);
    }
  }
}
